import { openModal } from "../../common/slices/modal.slice";
import { createSlice } from "@reduxjs/toolkit";
import { IAuthState } from "../interfaces/authCommon";
import { authApi } from "../../../api/auth.api";
import { emailApi } from "../../../api/email.api";
import {
  StorageService,
  EStorageKeys,
} from "../../common/utils/storage.service";
import { IEmailData } from "../../dashboard/interfaces/email";
import { DirectIds } from "../../../type/DirectIds";

const initialState: IAuthState = {
  token: "",
  isLoading: false,
  userLoading: false,
  termsLoaded: false,
  terms: "",
  user: {
    accept_terms_of_conditions: false,
    email: "",
    firstName: "",
    fullName: "",
    icon_id: "",
    id: "",
    lastName: "",
    organisation_type: "",
    role: "",
    last_calc_time: "",
    phone_number: "",
    company_name: "",
    job_title: "",
  },
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loading(state, action) {
      state.isLoading = action.payload;
    },
    userLoading(state, action) {
      state.userLoading = action.payload;
    },
    termsLoading(state, action) {
      state.termsLoaded = action.payload;
    },
    terms(state, action) {
      state.terms = action.payload.text;
      state.termsLoaded = false;
    },
    signIn(state, action) {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isLoading = false;
      StorageService.storeValue(EStorageKeys.token, action.payload.token);
      StorageService.storeValue(EStorageKeys.user_id, action.payload.user.id);
    },
    signOut(state) {
      state.user = initialState.user;
      state.token = "";
      state.isLoading = false;
      StorageService.clearStorage();
    },
    getUser(state, action) {
      state.user = action.payload.userFullRespItem;
      state.userLoading = false;
    },
    uploadProfileImage(state, action) {
      state.user.icon_id = action.payload.url;
    },
  },
});

export const fetchUser = (id: number) => async (dispatch: any) => {
  dispatch(userLoading(true));
  try {
    const res = await authApi.getUser(id);
    dispatch(getUser(res));
  } catch (e) {
    dispatch(userLoading(false));
  }
};

export const authSignIn = (data: any) => async (dispatch: any) => {
  dispatch(loading(true));
  try {
    const result = await authApi.signIn(data);
    const { AccessToken, RefreshToken } = result.AuthenticationResult;

    // Decode mysql_user_id from the access token payload (base64url)
    const b64 = AccessToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(b64));
    const userId = Number(payload["mysql_user_id"]);

    // Store tokens now so apiClient can attach the access token to the user fetch
    StorageService.storeValue(EStorageKeys.token, AccessToken);
    StorageService.storeValue(EStorageKeys.refresh_token, RefreshToken);

    const userRes = await authApi.getUser(userId);
    dispatch(signIn({ token: AccessToken, user: userRes.userFullRespItem }));
  } catch (e) {
    dispatch(loading(false));
  }
};

export const authSignOut = (success?: () => void) => async (dispatch: any) => {
  dispatch(loading(true));
  const accessToken = StorageService.getItem(EStorageKeys.token);
  try {
    if (accessToken) await authApi.signOut(accessToken);
  } catch {
    // GlobalSignOut failure (e.g. token already expired) is non-fatal
  }
  dispatch(signOut());
  success && success();
};

export const authSignUp =
  (data: any, success: () => void) => async (dispatch: any) => {
    const newUserNotification =
      "New registration: \n" +
      "  Name: " +
      data.firstName +
      "\n" +
      "  Last name: " +
      data.lastName +
      "\n" +
      "  Company name: " +
      data.companyName +
      "\n" +
      "  Email: " +
      data.email +
      "\n" +
      "  Phone number: " +
      data.phoneNumber +
      "\n" +
      "  Date: " +
      new Date().toUTCString() +
      "\n";

    dispatch(loading(true));
    try {
      await authApi.signUp(data);

      const memo2: IEmailData = {
        name: data.firstName + " " + data.lastName,
        comment: newUserNotification,
        email: data.email,
        contactWith: DirectIds.DI5_NEW_USER_NOTIFY, //'connor@heylari.com' //'matthew@heylari.com'
      };
      await emailApi.sendContactMeEmail(memo2);

      dispatch(loading(false));
      dispatch(
        openModal({
          modalTitle: "Success",
          modalContent: "Your account was registered successfully",
          buttonLabel: "Close",
          closable: false,
          buttonVisible: true,
        }),
      );
      success();
    } catch (e) {
      dispatch(loading(false));
    }
  };

export const authTerms = () => async (dispatch: any) => {
  dispatch(termsLoading(true));
  try {
    const res: any = await authApi.fetchTerms();
    dispatch(terms(res));
  } catch (e) {
    dispatch(termsLoading(false));
  }
};

export const authForgotPass =
  (data: any, onCodeSent: (email: string) => void) => async (dispatch: any) => {
    dispatch(loading(true));
    try {
      await authApi.forgotPassword(data);
      dispatch(loading(false));
      onCodeSent(data.email);
    } catch (e) {
      dispatch(loading(false));
    }
  };

export const authConfirmForgotPass =
  (
    email: string,
    code: string,
    password: string,
    success: () => void,
  ) =>
  async (dispatch: any) => {
    dispatch(loading(true));
    try {
      await authApi.confirmForgotPassword(email, code, password);
      dispatch(loading(false));
      dispatch(
        openModal({
          modalTitle: "Success",
          modalContent: "Password changed successfully",
          buttonLabel: "Close",
          closable: false,
          buttonVisible: true,
        }),
      );
      success();
    } catch (e) {
      dispatch(loading(false));
    }
  };

export const authResetPass =
  (data: any, success: () => void) => async (dispatch: any) => {
    dispatch(loading(true));
    try {
      await authApi.resetPassword(data);
      dispatch(loading(false));
      dispatch(
        openModal({
          modalTitle: "Success",
          modalContent: "Password changed successfully",
          buttonLabel: "Close",
          closable: false,
          buttonVisible: true,
        }),
      );
      success();
    } catch (e) {
      dispatch(loading(false));
    }
  };

const { actions, reducer } = authSlice;

export const {
  signIn,
  signOut,
  loading,
  userLoading,
  termsLoading,
  terms,
  getUser,
  uploadProfileImage,
} = actions;

export default reducer;
